import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {
  IShoppingCartResponse,
  IShoppingCartStep,
} from '@/shared/types/shopping-cart.types';
import { ICreateLeadPayload } from '../services/lead.service';

// vfs_fonts экспортирует объект vfs (Roboto с кириллицей). В 0.3.x подключаем вручную.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fonts: any = (pdfFonts as any).default ?? pdfFonts;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(pdfMake as any).addVirtualFileSystem(fonts);

const optionNames = (item: IShoppingCartStep): string[] => {
  const names = [
    ...(item.extra_options?.steps || []),
    ...(item.options?.steps || []),
  ]
    .map((s) => s?.name)
    .filter(Boolean) as string[];
  return Array.from(new Set(names));
};

interface ISpecRow {
  name: string;
  modification: string;
  options: string;
  quantity: number;
  amount: number;
}

const buildRows = (cart: IShoppingCartResponse): ISpecRow[] => {
  const map = new Map<string, ISpecRow>();
  cart.modifications.forEach((group) => {
    group.items.forEach((item) => {
      const options = optionNames(item).join(', ');
      const key = `${item.name}|${item.modification}|${options}`;
      const row = map.get(key);
      if (row) row.quantity += 1;
      else
        map.set(key, {
          name: item.name,
          modification: item.modification,
          options,
          quantity: 1,
          amount: item.amount,
        });
    });
  });
  return Array.from(map.values());
};

const money = (n: number) => Math.round(n).toLocaleString('ru-RU');

const formatDate = (d: Date) =>
  d.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

export const generateSpecPdf = (
  cart: IShoppingCartResponse,
  contact: ICreateLeadPayload,
  showPrices = false
) => {
  const rows = buildRows(cart);

  const contactLines: string[] = [];
  if (contact.full_name) contactLines.push(`ФИО: ${contact.full_name}`);
  contactLines.push(`Телефон: ${contact.phone}`);
  if (contact.email) contactLines.push(`Email: ${contact.email}`);
  if (contact.organization)
    contactLines.push(`Организация: ${contact.organization}`);

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 50, 40, 70],
    defaultStyle: { font: 'Roboto', fontSize: 11 },
    content: [
      { text: 'Спецификация конфигурации', style: 'h1' },
      {
        text: `Дата: ${formatDate(new Date())}`,
        margin: [0, 0, 0, 12],
        color: '#555',
      },
      { text: 'Контактные данные', style: 'h2' },
      {
        text: contactLines.join('\n'),
        margin: [0, 0, 0, 14],
      },
      ...(contact.comment
        ? [
            { text: 'Комментарий', style: 'h2' },
            { text: contact.comment, margin: [0, 0, 0, 14] },
          ]
        : []),
      { text: 'Состав конфигурации', style: 'h2' },
      {
        table: {
          headerRows: 1,
          widths: showPrices
            ? [14, '*', 'auto', '*', 32, 'auto', 'auto']
            : [16, '*', 'auto', '*', 36],
          body: [
            [
              { text: '№', style: 'th' },
              { text: 'Модель', style: 'th' },
              { text: 'Модификация', style: 'th' },
              { text: 'Опции', style: 'th' },
              { text: 'Кол-во', style: 'th' },
              ...(showPrices
                ? [
                    { text: 'Цена за ед., ₽', style: 'th' },
                    { text: 'Сумма, ₽', style: 'th' },
                  ]
                : []),
            ],
            ...rows.map((r, i) => [
              `${i + 1}`,
              r.name,
              r.modification,
              r.options || '—',
              { text: `${r.quantity}`, alignment: 'center' },
              ...(showPrices
                ? [
                    { text: money(r.amount), alignment: 'right' },
                    { text: money(r.amount * r.quantity), alignment: 'right' },
                  ]
                : []),
            ]),
          ],
        },
        layout: 'lightHorizontalLines',
      },
      ...(showPrices
        ? [
            {
              text: `Итого: ${money(cart.total_amount)} ₽`,
              alignment: 'right',
              bold: true,
              fontSize: 13,
              margin: [0, 12, 0, 0],
            },
            {
              text: 'Цены ориентировочные, без НДС. Окончательную стоимость подтверждает менеджер.',
              color: '#888',
              fontSize: 9,
              margin: [0, 4, 0, 0],
            },
          ]
        : []),
    ],
    footer: (currentPage: number, pageCount: number) => ({
      margin: [40, 10, 40, 0],
      stack: [
        {
          text: 'Документ носит информационный характер и не является офертой. Окончательную стоимость, наличие и условия поставки уточняйте у менеджера.',
          fontSize: 8,
          color: '#888',
        },
        {
          text: `${currentPage} / ${pageCount}`,
          alignment: 'right',
          fontSize: 8,
          color: '#888',
          margin: [0, 2, 0, 0],
        },
      ],
    }),
    styles: {
      h1: { fontSize: 18, bold: true, margin: [0, 0, 0, 8] },
      h2: { fontSize: 13, bold: true, margin: [0, 6, 0, 6] },
      th: { bold: true, fillColor: '#F0F5F2', margin: [0, 2, 0, 2] },
    },
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (pdfMake as any).createPdf(docDefinition).download('specifikaciya.pdf');
};
